/**
 * Spotlight Overlay Component
 * Full-screen overlay that highlights target elements with cutout effect
 */

import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject, takeUntil } from 'rxjs';
import { WorkflowPlayerService } from '../../guide/workflow-player.service';
import { SpotlightConfig } from '../../models/workflow.models';
import { StepTooltipComponent } from '../step-tooltip/step-tooltip.component';

@Component({
  selector: 'ai-spotlight-overlay',
  templateUrl: './spotlight-overlay.component.html',
  styleUrls: ['./spotlight-overlay.component.scss'],
  standalone: true,
  imports: [CommonModule, StepTooltipComponent]
})
export class SpotlightOverlayComponent implements OnInit, OnDestroy {

  isVisible = false;
  config: SpotlightConfig | null = null;
  private currentRect: DOMRect | null = null;

  // Calculated positions
  highlightStyle: any = {};
  backdropClipPath = '';

  private destroy$ = new Subject<void>();

  constructor(private workflowPlayer: WorkflowPlayerService) {}

  ngOnInit(): void {
    // Subscribe to spotlight config changes
    this.workflowPlayer.spotlightConfig$
      .pipe(takeUntil(this.destroy$))
      .subscribe(config => {
        this.config = config;
        this.isVisible = config !== null;

        this.refreshPositions();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Handle window resize
   */
  @HostListener('window:resize')
  onResize(): void {
    this.refreshPositions();
  }

  @HostListener('window:scroll')
  onScroll(): void {
    this.refreshPositions();
  }

  /**
   * Handle backdrop click
   */
  onBackdropClick(): void {
    // Could allow clicking backdrop to skip, but for now we don't do anything
    // User must use the tooltip actions
  }

  /**
   * Update positions based on config
   */
  private updatePositions(config: SpotlightConfig): void {
    const rect = config.targetElement?.getBoundingClientRect() || config.targetRect;
    if (!rect) {
      return;
    }
    this.currentRect = rect;
    const padding = 8; // Padding around highlighted element

    // Calculate highlight ring position
    this.highlightStyle = {
      top: `${rect.top - padding}px`,
      left: `${rect.left - padding}px`,
      width: `${rect.width + padding * 2}px`,
      height: `${rect.height + padding * 2}px`
    };

    // Create clip-path for backdrop cutout
    // We'll use polygon to create a cutout effect
    this.backdropClipPath = this.createClipPath(rect, padding);
  }

  private refreshPositions(): void {
    if (!this.config) return;

    if (this.config.targetElement) {
      this.config.targetRect = this.config.targetElement.getBoundingClientRect();
    }

    this.updatePositions(this.config);
  }

  /**
   * Create CSS clip-path for cutout effect
   * Creates a polygon that covers the viewport except for the cutout area
   */
  private createClipPath(rect: DOMRect, padding: number): string {
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    const cutoutTop = rect.top - padding;
    const cutoutBottom = rect.bottom + padding;
    const cutoutLeft = rect.left - padding;
    const cutoutRight = rect.right + padding;

    // Create a polygon path that goes around the viewport and has a hole for the element
    // This is achieved using the evenodd fill rule
    const points = [
      // Outer rectangle (viewport)
      `0 0`,
      `${vw}px 0`,
      `${vw}px ${vh}px`,
      `0 ${vh}px`,
      `0 0`,
      // Inner rectangle (cutout) - reversed direction
      `${cutoutLeft}px ${cutoutTop}px`,
      `${cutoutLeft}px ${cutoutBottom}px`,
      `${cutoutRight}px ${cutoutBottom}px`,
      `${cutoutRight}px ${cutoutTop}px`,
      `${cutoutLeft}px ${cutoutTop}px`
    ];

    return `polygon(evenodd, ${points.join(', ')})`;
  }

  /**
   * Get tooltip style position
   */
  get tooltipStyle(): any {
    const rect = this.currentRect;
    if (!rect) return {};
    const position = this.config.tooltipPosition;
    const offset = 20;
    const tooltipWidth = 320;
    const tooltipHeight = 200;
    const margin = 12;

    const clamp = (value: number, min: number, max: number) =>
      Math.min(Math.max(value, min), max);

    let top: number;
    let left: number;

    switch (position) {
      case 'bottom':
        top = rect.bottom + offset;
        left = rect.left + rect.width / 2 - tooltipWidth / 2;
        break;
      case 'top':
        top = rect.top - offset - tooltipHeight;
        left = rect.left + rect.width / 2 - tooltipWidth / 2;
        break;
      case 'right':
        top = rect.top + rect.height / 2 - tooltipHeight / 2;
        left = rect.right + offset;
        break;
      case 'left':
        top = rect.top + rect.height / 2 - tooltipHeight / 2;
        left = rect.left - offset - tooltipWidth;
        break;
      default:
        top = rect.bottom + offset;
        left = rect.left + rect.width / 2 - tooltipWidth / 2;
    }

    top = clamp(top, margin, window.innerHeight - tooltipHeight - margin);
    left = clamp(left, margin, window.innerWidth - tooltipWidth - margin);

    return {
      position: 'fixed',
      zIndex: 10002,
      top: `${top}px`,
      left: `${left}px`,
      maxWidth: `${tooltipWidth}px`
    };
  }

  /**
   * Handle next button click
   */
  onNext(): void {
    this.workflowPlayer.nextStep();
  }

  /**
   * Handle back button click
   */
  onBack(): void {
    this.workflowPlayer.previousStep();
  }

  /**
   * Handle skip button click
   */
  onSkip(): void {
    this.workflowPlayer.cancelWorkflow('User skipped workflow');
  }
}
